import React from "react";
import { BrowserRouter, Route, Routes, Link } from "react-router-dom";
import CityPage from "./CityPage";
import OfficeRedirect from "./OfficeRedirect";
import { useState, useEffect } from "react";
import {
  ContentLayout,
  Header,
  TopNavigation,
} from "@cloudscape-design/components";

interface City {
  name: string;
}

interface CityConfig {
  name: string;
  feedbackAlias: string;
  feedbackFirstName: string;
  offices: {
    code: string;
    url: string;
  }[];
}

const Frame: React.FC<React.PropsWithChildren> = ({ children }) => (
  <>
    <TopNavigation
      identity={{
        href: "/",
        title: "Amazon Coffee Shuffle",
        logo: { src: "/logo192.png" },
      }}
    />
    <ContentLayout maxContentWidth={1000} defaultPadding={true}>
      {children}
    </ContentLayout>
  </>
);

function formatCityName(cityKey: string): string {
  return cityKey
    .split("-")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

const App: React.FC = () => {
  const [cities, setCities] = useState<{ cities: City[] }>({ cities: [] });

  useEffect(() => {
    // Fetch cities from API
    const fetchCities = async () => {
      try {
        const response = await fetch("/availableCities");
        const data = await response.json();
        setCities(data);
      } catch (error) {
        console.error("Error fetching cities:", error);
      }
    };

    fetchCities();
  }, []);

  const HomePage: React.FC = () => (
    <div>
      <Header variant="h1">Welcome to Amazon Coffee Shuffle</Header>
      <p>A mechanism for connecting Amazonians on different campuses.</p>
      <ul>
        {cities.cities.map((city) => (
          <li key={city.name}>
            <Link to={`/${city.name.toLowerCase()}`}>
              {formatCityName(city.name)}
            </Link>
          </li>
        ))}
      </ul>
      <p>
        Want to add your own city? Please reach out to uhinze@ to get started.
      </p>
    </div>
  );

  const CityPageWrapper: React.FC = () => {
    const [cityConfig, setCityConfig] = useState<CityConfig>();
    const cityName = window.location.pathname.slice(1);

    useEffect(() => {
      // Fetch offices for the specific city from API
      const fetchOffices = async () => {
        try {
          const response = await fetch(`/cities/${cityName}/config`);
          const data = await response.json();
          setCityConfig(data);
        } catch (error) {
          console.error("Error fetching offices:", error);
        }
      };

      fetchOffices();
    }, [cityName]);

    if (!cityConfig) return <div>Loading...</div>;

    return (
      <CityPage
        cityName={formatCityName(cityName)}
        offices={cityConfig.offices}
        feedbackAlias={cityConfig.feedbackAlias}
        feedbackFirstName={cityConfig.feedbackFirstName}
      />
    );
  };

  return (
    <Frame>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/:city" element={<CityPageWrapper />} />
          <Route path="/:city/:office" element={<OfficeRedirect />} />
        </Routes>
      </BrowserRouter>
    </Frame>
  );
};

export default App;
